/**
 * The online client (src/net + src/online-ui, ≈ 290 KB minified) stays out of the main bundle and loads when the
 * player goes online (docs/PERF.md "Load time"): "Play online" on the title screen, or a test calling one of
 * the online hooks. Until then six stand-in systems hold the real ones' slots (same ids, same `order`), the
 * hooks the online client registers answer by loading it first, and `net` / `online` / `onlineUi` are simply
 * not provided, which every reader already takes as "offline".
 *
 * Once loaded, the real systems are initialised and started in slot order, and from then on the stand-ins
 * pass every fixedUpdate / update / dispose through, so the frame runs them exactly where it did before.
 * Without a DOM (headless tests, the soak) the client loads during boot, inside each slot's init, as if it had
 * never been split.
 */
import type { BhHooks } from '../core/hooks';
import type { GameContext } from '../core/context';
import type { System, SystemFactory } from '../core/system';
// The online client's stylesheets stay in the main bundle, in the order they always had: a sheet added when
// the chunk lands would come after every other one and win the ties it used to lose.
import '../online-ui/online.css';
import '../online-ui/panels.css';
import '../online-ui/market.css';
import '../online-ui/gang.css';
import '../online-ui/territory.css';
import '../online-ui/social.css';
import '../online-ui/freight.css';
import '../online-ui/firmJob.css';
import '../online-ui/touch.css';
import '../online-ui/events.css';

export interface OnlineClientService {
  /** The online client's code is in and its systems run. */
  readonly loaded: boolean;
  /** Fetch and start the online client (once; a failed fetch can be tried again). */
  load(): Promise<void>;
  /** Run `fn` now if the online client is in, else as soon as it is. */
  whenLoaded(fn: () => void): void;
}

declare module '../core/services' {
  interface ServiceMap {
    onlineClient: OnlineClientService;
  }
}

/** The real systems' ids and orders (onlineBundle.ts builds them in this order). */
export const ONLINE_SLOTS: readonly { id: string; order: number }[] = [
  { id: 'net-in', order: 190 }, // corrections, resume, car hand-back
  { id: 'net-proxy', order: 395 }, // remote cars → interpolated server pose, before physics
  { id: 'net-send', order: 455 }, // upload predicted pose at 20 Hz
  { id: 'net-view', order: 712 }, // other players on foot
  { id: 'online-ui', order: 756 }, // sign-in, connection chip, reconnect overlay, name tags
  { id: 'online-game', order: 757 }, // online gameplay UI: wallet/bank/cargo HUD, panels, places, gang, chat, coop
];

/** Every hook the online client registers (a test checks the list against the real systems). */
export const ONLINE_HOOKS = [
  'netStatus',
  'netConnect',
  'remotePlayers',
  'netStats',
  'netDrop',
  'netDisconnect',
  'netVehicles',
  'remoteFigures',
  'onlineUiState',
  'wallet',
  'inventory',
  'gang',
  'heatOnline',
  'requestAction',
  'onlineState',
  'openPanel',
  'closePanel',
] as const satisfies readonly (keyof BhHooks)[];

type Bundle = typeof import('./onlineBundle');
let bundle: Promise<Bundle> | null = null;

function loadBundle(): Promise<Bundle> {
  bundle ??= import('./onlineBundle').catch((e: unknown) => {
    bundle = null;
    throw e;
  });
  return bundle;
}

/** Stand-ins for SYSTEMS (src/game/systems.ts), one per ONLINE_SLOTS entry, sharing one online client. */
export function createOnlineSystemFactories(): SystemFactory[] {
  let set: ReturnType<typeof createSet> | null = null;
  return ONLINE_SLOTS.map((_, i) => (): System => {
    // A fresh Game builds a fresh set: rebuild when the first factory is called again.
    if (i === 0 || !set) set = createSet();
    return set.slots[i]!;
  });
}

function createSet(): { slots: System[] } {
  const eager = typeof document === 'undefined';
  let ctx: GameContext | null = null;
  let real: System[] | null = null;
  let live = false;
  let loading: Promise<void> | null = null;
  let disposed = false;
  const waiting: (() => void)[] = [];

  const build = async (): Promise<System[]> => {
    real ??= (await loadBundle()).createOnlineSystems();
    return real;
  };

  const markLive = (): void => {
    live = true;
    for (const fn of waiting.splice(0)) fn();
  };

  function load(): Promise<void> {
    if (live) return Promise.resolve();
    loading ??= (async () => {
      const systems = await build();
      const c = ctx;
      if (!c || disposed) return;
      for (const s of systems) await s.init?.(c);
      for (const s of systems) s.start?.();
      markLive();
    })().catch((e: unknown) => {
      loading = null;
      throw e;
    });
    return loading;
  }

  const service: OnlineClientService = {
    get loaded() {
      return live;
    },
    load,
    whenLoaded(fn) {
      if (live) fn();
      else waiting.push(fn);
    },
  };

  /** Before the client is in: the hooks load it, then answer through the real registration. */
  function registerStandIns(c: GameContext): void {
    for (const name of ONLINE_HOOKS) {
      const standIn = async (...args: unknown[]): Promise<unknown> => {
        await load();
        const fn = c.hooks.api[name];
        if (fn === standIn || typeof fn !== 'function') throw new Error(`__bh.${name} was not registered by the online client`);
        return (fn as (...a: unknown[]) => unknown)(...args);
      };
      c.hooks.register(name, standIn as never);
    }
    c.hooks.registerSnapshot('net', () => ({ status: 'offline', online: false, eid: 0, remotes: 0, vehicles: 0 }));
  }

  const slots = ONLINE_SLOTS.map(
    ({ id, order }, i): System => ({
      id,
      order,
      async init(c) {
        if (i === 0) {
          ctx = c;
          c.services.provide('onlineClient', service);
          if (!eager) registerStandIns(c);
        }
        if (eager) await (await build())[i]!.init?.(c);
      },
      start() {
        if (!eager) return;
        real![i]!.start?.();
        if (i === ONLINE_SLOTS.length - 1) markLive();
      },
      fixedUpdate(dt) {
        if (live) real![i]!.fixedUpdate?.(dt);
      },
      update(dt, alpha) {
        if (live) real![i]!.update?.(dt, alpha);
      },
      dispose() {
        disposed = true;
        if (live) real![i]!.dispose?.();
      },
    }),
  );
  return { slots };
}
